import React, { useState, useEffect } from 'react';
import MainLayout from '../../components/layout/MainLayout';
import { MdAdd, MdEdit, MdDelete, MdSearch, MdVisibility, MdPrint, MdCheck } from 'react-icons/md';
import '../master/MasterData.css';

interface Customer {
  customerId: number;
  customerCode: string;
  customerName: string;
  customerType: string;
  tin: string;
  email: string;
  phone: string;
  city: string;
  isActive: string;
}

interface DeliveryItem {
  itemId: number;
  itemCode: string;
  itemName: string;
  orderedQty: number;
  deliveredQty: number;
  unitPrice: number;
  amount: number;
}

interface DeliveryReceipt {
  deliveryId: number;
  deliveryNo: string;
  deliveryDate: string;
  orderNo: string;
  customerCode: string;
  customerName: string;
  shippingAddress: string;
  totalAmount: number;
  status: 'PREPARING' | 'IN_TRANSIT' | 'DELIVERED' | 'CONFIRMED';
  items: DeliveryItem[];
  deliveryPerson: string;
  vehicleNo: string;
  remarks: string;
}

const DeliveryReceipts: React.FC = () => {
  const [deliveries, setDeliveries] = useState<DeliveryReceipt[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState<DeliveryReceipt | null>(null);
  const [viewMode, setViewMode] = useState(false);

  useEffect(() => {
    // TODO: Fetch customers from API
    setCustomers([
      {
        customerId: 1,
        customerCode: 'CUST001',
        customerName: 'SM Furniture Store',
        customerType: 'CORPORATE',
        tin: '123-456-789-000',
        email: 'sm@furniture.ph',
        phone: '+63 2 1234 5678',
        city: 'Manila',
        isActive: 'Y'
      },
      {
        customerId: 2,
        customerCode: 'CUST002',
        customerName: 'Home Depot Philippines',
        customerType: 'CORPORATE',
        tin: '987-654-321-000',
        email: 'homedepot@example.ph',
        phone: '+63 2 9876 5432',
        city: 'Quezon City',
        isActive: 'Y'
      }
    ]);

    // TODO: Fetch delivery receipts from API
    setDeliveries([
      {
        deliveryId: 1,
        deliveryNo: 'DR-2025-0001',
        deliveryDate: '2025-02-10',
        orderNo: 'SO-2025-0001',
        customerCode: 'CUST001',
        customerName: 'SM Furniture Store',
        shippingAddress: '123 Main St, Manila',
        totalAmount: 450000,
        status: 'DELIVERED',
        items: [],
        deliveryPerson: 'Juan Dela Cruz',
        vehicleNo: 'ABC-1234',
        remarks: 'Delivered successfully'
      }
    ]);
  }, []);

  const filteredDeliveries = deliveries.filter(delivery =>
    delivery.deliveryNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
    delivery.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    delivery.orderNo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAdd = () => {
    setEditingDelivery(null);
    setViewMode(false);
    setShowModal(true);
  };

  const handleEdit = (delivery: DeliveryReceipt) => {
    setEditingDelivery(delivery);
    setViewMode(false);
    setShowModal(true);
  };

  const handleView = (delivery: DeliveryReceipt) => {
    setEditingDelivery(delivery);
    setViewMode(true);
    setShowModal(true);
  };

  const handleDelete = (deliveryId: number) => {
    if (window.confirm('Are you sure you want to delete this delivery receipt?')) {
      setDeliveries(deliveries.filter(d => d.deliveryId !== deliveryId));
    }
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'CONFIRMED': return 'badge-success';
      case 'DELIVERED': return 'badge-info';
      case 'IN_TRANSIT': return 'badge-warning';
      default: return 'badge-secondary';
    }
  };

  return (
    <MainLayout>
      <div className="master-data-page">
        <div className="page-header">
          <div>
            <h2>Delivery Receipts</h2>
            <p className="subtitle">Track product deliveries and shipments</p>
          </div>
          <button className="btn btn-primary" onClick={handleAdd}>
            <MdAdd /> Create Delivery Receipt
          </button>
        </div>

        <div className="page-content">
          <div className="table-controls">
            <div className="search-box">
              <MdSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search deliveries..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="data-table-container">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Delivery No</th>
                  <th>Delivery Date</th>
                  <th>Order No</th>
                  <th>Customer</th>
                  <th>Total Amount</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDeliveries.map(delivery => (
                  <tr key={delivery.deliveryId}>
                    <td className="code">{delivery.deliveryNo}</td>
                    <td>{new Date(delivery.deliveryDate).toLocaleDateString('en-US')}</td>
                    <td>{delivery.orderNo}</td>
                    <td>{delivery.customerName}</td>
                    <td className="amount">₱{delivery.totalAmount.toLocaleString()}</td>
                    <td>
                      <span className={`badge ${getStatusBadgeClass(delivery.status)}`}>
                        {delivery.status.replace('_', ' ')}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon btn-view"
                          onClick={() => handleView(delivery)}
                          title="View"
                        >
                          <MdVisibility />
                        </button>
                        <button
                          className="btn-icon btn-edit"
                          onClick={() => handleEdit(delivery)}
                          title="Edit"
                        >
                          <MdEdit />
                        </button>
                        <button
                          className="btn-icon btn-print"
                          onClick={() => window.print()}
                          title="Print"
                        >
                          <MdPrint />
                        </button>
                        <button
                          className="btn-icon btn-delete"
                          onClick={() => handleDelete(delivery.deliveryId)}
                          title="Delete"
                        >
                          <MdDelete />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredDeliveries.length === 0 && (
              <div className="no-data">
                <p>No delivery receipts found</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {showModal && (
        <DeliveryModal
          delivery={editingDelivery}
          viewMode={viewMode}
          customers={customers}
          onClose={() => setShowModal(false)}
          onSave={(delivery) => {
            if (editingDelivery) {
              setDeliveries(deliveries.map(d =>
                d.deliveryId === delivery.deliveryId ? delivery : d
              ));
            } else {
              setDeliveries([...deliveries, { ...delivery, deliveryId: Date.now() }]);
            }
            setShowModal(false);
          }}
        />
      )}
    </MainLayout>
  );
};

interface DeliveryModalProps {
  delivery: DeliveryReceipt | null;
  viewMode: boolean;
  customers: Customer[];
  onClose: () => void;
  onSave: (delivery: DeliveryReceipt) => void;
}

const DeliveryModal: React.FC<DeliveryModalProps> = ({ delivery, viewMode, customers, onClose, onSave }) => {
  const [formData, setFormData] = useState<DeliveryReceipt>(
    delivery || {
      deliveryId: 0,
      deliveryNo: '',
      deliveryDate: new Date().toISOString().split('T')[0],
      orderNo: '',
      customerCode: '',
      customerName: '',
      shippingAddress: '',
      totalAmount: 0,
      status: 'PREPARING',
      items: [],
      deliveryPerson: '',
      vehicleNo: '',
      remarks: ''
    }
  );

  const handleCustomerChange = (customerCode: string) => {
    const selectedCustomer = customers.find(c => c.customerCode === customerCode);
    if (selectedCustomer) {
      setFormData({
        ...formData,
        customerCode: selectedCustomer.customerCode,
        customerName: selectedCustomer.customerName
      });
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!viewMode) {
      onSave(formData);
    }
  };

  const handleAddItem = () => {
    setFormData({
      ...formData,
      items: [
        ...formData.items,
        {
          itemId: Date.now(),
          itemCode: '',
          itemName: '',
          orderedQty: 0,
          deliveredQty: 0,
          unitPrice: 0,
          amount: 0
        }
      ]
    });
  };

  const handleRemoveItem = (itemId: number) => {
    setFormData({
      ...formData,
      items: formData.items.filter(item => item.itemId !== itemId)
    });
  };

  const handleItemChange = (itemId: number, field: keyof DeliveryItem, value: any) => {
    setFormData({
      ...formData,
      items: formData.items.map(item => {
        if (item.itemId === itemId) {
          const updatedItem = { ...item, [field]: value };
          if (field === 'deliveredQty' || field === 'unitPrice') {
            updatedItem.amount = updatedItem.deliveredQty * updatedItem.unitPrice;
          }
          return updatedItem;
        }
        return item;
      })
    });
  };

  useEffect(() => {
    const total = formData.items.reduce((sum, item) => sum + item.amount, 0);
    setFormData(prev => ({ ...prev, totalAmount: total }));
  }, [formData.items]);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>{viewMode ? 'View Delivery Receipt' : delivery ? 'Edit Delivery Receipt' : 'Create Delivery Receipt'}</h3>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Delivery No *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.deliveryNo}
                  onChange={(e) => setFormData({ ...formData, deliveryNo: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Delivery Date *</label>
                <input
                  type="date"
                  lang="en-US"
                  required
                  disabled={viewMode}
                  value={formData.deliveryDate}
                  onChange={(e) => setFormData({ ...formData, deliveryDate: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Order No *</label>
                <input
                  type="text"
                  required
                  disabled={viewMode}
                  value={formData.orderNo}
                  onChange={(e) => setFormData({ ...formData, orderNo: e.target.value })}
                />
              </div>
            </div>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label>Customer Name *</label>
                <select
                  required
                  disabled={viewMode}
                  value={formData.customerCode}
                  onChange={(e) => handleCustomerChange(e.target.value)}
                >
                  <option value="">Select Customer</option>
                  {customers.filter(c => c.isActive === 'Y').map(customer => (
                    <option key={customer.customerId} value={customer.customerCode}>
                      {customer.customerName}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Customer Code *</label>
                <input
                  type="text"
                  required
                  disabled
                  value={formData.customerCode}
                  readOnly
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  disabled={viewMode}
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                >
                  <option value="PREPARING">Preparing</option>
                  <option value="IN_TRANSIT">In Transit</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="CONFIRMED">Confirmed</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Shipping Address *</label>
              <textarea
                required
                disabled={viewMode}
                value={formData.shippingAddress}
                onChange={(e) => setFormData({ ...formData, shippingAddress: e.target.value })}
                rows={2}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Delivery Person</label>
                <input
                  type="text"
                  disabled={viewMode}
                  value={formData.deliveryPerson}
                  onChange={(e) => setFormData({ ...formData, deliveryPerson: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Vehicle No</label>
                <input
                  type="text"
                  disabled={viewMode}
                  value={formData.vehicleNo}
                  onChange={(e) => setFormData({ ...formData, vehicleNo: e.target.value })}
                />
              </div>
            </div>

            <div className="form-section">
              <div className="section-header">
                <h4>Delivery Items</h4>
                {!viewMode && (
                  <button type="button" className="btn btn-sm btn-primary" onClick={handleAddItem}>
                    <MdAdd /> Add Item
                  </button>
                )}
              </div>

              <table className="item-table">
                <thead>
                  <tr>
                    <th>Item Code</th>
                    <th>Item Name</th>
                    <th>Ordered Qty</th>
                    <th>Delivered Qty</th>
                    <th>Unit Price</th>
                    <th>Amount</th>
                    {!viewMode && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {formData.items.map(item => (
                    <tr key={item.itemId}>
                      <td>
                        <input
                          type="text"
                          disabled={viewMode}
                          value={item.itemCode}
                          onChange={(e) => handleItemChange(item.itemId, 'itemCode', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="text"
                          disabled={viewMode}
                          value={item.itemName}
                          onChange={(e) => handleItemChange(item.itemId, 'itemName', e.target.value)}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.orderedQty}
                          onChange={(e) => handleItemChange(item.itemId, 'orderedQty', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.deliveredQty}
                          onChange={(e) => handleItemChange(item.itemId, 'deliveredQty', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          disabled={viewMode}
                          value={item.unitPrice}
                          onChange={(e) => handleItemChange(item.itemId, 'unitPrice', parseFloat(e.target.value))}
                        />
                      </td>
                      <td>₱{item.amount.toLocaleString()}</td>
                      {!viewMode && (
                        <td>
                          <button
                            type="button"
                            className="btn-icon btn-delete"
                            onClick={() => handleRemoveItem(item.itemId)}
                          >
                            <MdDelete />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5} style={{ textAlign: 'right', fontWeight: 'bold' }}>Total:</td>
                    <td style={{ fontWeight: 'bold' }}>₱{formData.totalAmount.toLocaleString()}</td>
                    {!viewMode && <td></td>}
                  </tr>
                </tfoot>
              </table>
            </div>

            <div className="form-group">
              <label>Remarks</label>
              <textarea
                disabled={viewMode}
                value={formData.remarks}
                onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {viewMode ? 'Close' : 'Cancel'}
            </button>
            {!viewMode && (
              <button type="submit" className="btn btn-primary">
                {delivery ? 'Update' : 'Create'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeliveryReceipts;
